/**
 * Lo reciente de producción en todos los teléfonos de la estación (docs/esquema.md §5.5 y §12.20;
 * pedido de Agata del 20/10/2026: que Horacio y Jonathan, de mantenimiento, y las encargadas vean
 * «las últimas veinticuatro horas de lo que hicieron» entre turnos). El servidor baja a un teléfono
 * de producción `produccion_reciente`: los silos cuya apertura o cierre cae en la ventana que ÉL
 * fija, las pesadas de turno, las paradas y las bajadas de carro vigentes, de todos los teléfonos;
 * acá se funden por identidad con lo propio del teléfono, con los bordes del snapshot.
 *
 * Pesadas, paradas y bajadas siguen las reglas 0 a 4 de verde (`reciente.ts`). El silo NO: en
 * verde el teléfono solo crea, así que «la raíz está en el catálogo» quiere decir «ya reflejado»;
 * en producción un recibo de `llenado.cerrar` es una TRANSICIÓN sobre una identidad que el catálogo
 * ya tiene, y un silo abierto ayer que este teléfono cerró hace un minuto tiene que verse cerrado.
 * Por eso el silo va por máquina de estados, como `proyectarSilosAbiertos` en el inicio: la fila
 * del bloque es la base y lo propio no reflejado la muta.
 */

import { entraElPropio, marcaDePendiente, ordenDelAcuse, ordenarRecientes, personasDe, ubicarEnVentana, type FilaBase } from './reciente';
import type { Turno } from './tiempo';

export type TipoDeLote = 'campo' | 'blendeo';
export type EstadoDeSilo = 'abierto' | 'cerrado' | 'descartado';

/** Un silo del bloque: la versión vigente, con quién lo abrió y quién lo cerró o descartó. */
export interface SiloReciente {
	llenado_id: string;
	version_id: string;
	campania_id: string;
	precinto: string;
	silo_fisico_id: string;
	trieur_id: string;
	lote_campo_id: string;
	lote_tipo: TipoDeLote;
	abierto_en: string;
	cerrado_en: string | null;
	estado: EstadoDeSilo;
	motivo_descarte: string | null;
	tara_base_g: number;
	/** El bruto del último parcial vigente, o null sin pesada. */
	ultimo_bruto_g: number | null;
	/** `ultimo_bruto_g − tara_base_g`, o null sin pesada. */
	neto_g: number | null;
	/** La persona de la raíz (la versión con `corrige_a IS NULL`): quien abrió. */
	abierto_por: string;
	/** La persona de la vigente: quien cerró o descartó; en un abierto, quien abrió. */
	persona_id: string;
	dispositivo_id: string;
	/** 1 si esta versión corrige a otra (`corrige_a IS NOT NULL`): «corregido por la oficina». */
	corregido: number;
}

/** Una pesada de turno (o la del cierre) del bloque, con los kilos del turno ya calculados. */
export interface PesadaReciente {
	raiz_id: string;
	version_id: string;
	llenado_id: string;
	campania_id: string;
	precinto: string;
	silo_fisico_id: string;
	estado_del_silo: EstadoDeSilo;
	lote_tipo: TipoDeLote;
	turno: Turno;
	inicio_en: string;
	fin_en: string;
	bruto_g: number;
	/** El bruto menos el del parcial vigente anterior del silo, o la tara base: la cuenta de `CTE_PESADAS`. */
	del_turno_g: number;
	/** 1 si es la pesada del cierre (`fin_en = cerrado_en` de la vigente). */
	es_cierre: number;
	entregada_por: string;
	recibida_por: string | null;
	dispositivo_id: string;
	corregido: number;
}

export interface ParadaReciente {
	raiz_id: string;
	version_id: string;
	campania_id: string;
	motivo_parada_id: string;
	minutos: number;
	turno: Turno;
	/** El inicio del turno informado, no la hora del tipeo (§4.1). */
	registrado_en: string;
	/** «Qué se rompió»: lo que mantenimiento quiere ver. */
	notas: string | null;
	persona_id: string;
	dispositivo_id: string;
	corregido: number;
}

export interface BajadaReciente {
	raiz_id: string;
	version_id: string;
	campania_id: string;
	carro_id: string;
	registrado_en: string;
	persona_id: string;
	dispositivo_id: string;
	corregido: number;
}

export interface ProduccionReciente {
	/** `YYYY-MM-DDTHH:MM` en hora del negocio: el instante del servidor menos 24 h. */
	desde: string;
	/** El instante del servidor más la tolerancia. */
	hasta: string;
	silos: SiloReciente[];
	pesadas: PesadaReciente[];
	paradas: ParadaReciente[];
	bajadas: BajadaReciente[];
}

/**
 * Lo que el resumen de la cola trae y acá se usa (`ResumenLlenado`, `ResumenParcial`, `ResumenParada`,
 * `ResumenCambioCarro`). El intervalo del cierre lleva `lote_campo_id` desde el 20/10 (ronda de disensor
 * sobre el plan, h1): sin él, la fila reconstruida desde el resumen no sabía si la pesada suma.
 */
export interface ResumenPropio {
	entidad: string;
	accion?: string;
	silo?: string;
	precinto?: string;
	neto_g?: number | null;
	intervalo?: { silo_fisico_id: string; trieur_id: string; abierto_en: string; lote_campo_id?: string };
	bruto_g?: number;
	del_turno_g?: number;
	motivo?: string;
	minutos?: number;
	carro?: string;
}

/**
 * Lo propio de este teléfono, como lo entrega la cola, EN ORDEN DE APLICACIÓN: los recibos por su
 * acuse y después las pendientes por cola. `hora` es la hora del hecho, sacada del payload
 * (`abierto_en`, `cerrado_en`, `fin_en`, `registrado_en`); null en un descarte de un silo ya cerrado.
 */
export interface PropioDeProduccion {
	entidad: 'llenado' | 'parcial' | 'parada' | 'cambio_carro';
	tipo: string;
	payload: unknown;
	resumen: ResumenPropio;
	personas: string[];
	registro_id: string;
	/** La raíz que trajo el acuse; null en una pendiente, que todavía no tiene. */
	raiz_id: string | null;
	hora: string | null;
	/** Solo en un recibo: cuándo lo acusó el servidor (ISO UTC). */
	sincronizado_en?: string;
	pendiente: boolean;
	id?: string;
	epoch?: string;
}

export interface FilaSilo extends FilaBase {
	entidad: 'llenado';
	llenado_id: string;
	precinto: string;
	/** El número del silo físico como texto, si lo propio lo trae; si no, se resuelve por `silo_fisico_id`. */
	silo: string | null;
	silo_fisico_id: string | null;
	trieur_id: string | null;
	lote_campo_id: string | null;
	lote_tipo: TipoDeLote | null;
	estado: EstadoDeSilo;
	abierto_en: string | null;
	cerrado_en: string | null;
	tara_base_g: number | null;
	ultimo_bruto_g: number | null;
	neto_g: number | null;
	motivo_descarte: string | null;
}

/** Por qué una pesada no entra en «Kilos de turno»; null si suma. Sin dato no suma (ronda de disensor, 20/10). */
export type MotivoNoSuma = 'blendeo' | 'descartado' | 'lote_desconocido';

export interface FilaPesada extends FilaBase {
	entidad: 'parcial';
	llenado_id: string;
	precinto: string;
	silo_fisico_id: string | null;
	turno: Turno | null;
	fin_en: string | null;
	bruto_g: number;
	del_turno_g: number;
	es_cierre: boolean;
	entregada_por: string | null;
	recibida_por: string | null;
	/** La regla de `CTE_PESADAS`: suma solo con lote de campo y silo no descartado; sin saber el lote, no suma. */
	no_suma: MotivoNoSuma | null;
}

export interface FilaParada extends FilaBase {
	entidad: 'parada';
	motivo_parada_id: string | null;
	/** El nombre del motivo, si lo propio lo trae; si no, se resuelve por `motivo_parada_id`. */
	motivo: string | null;
	minutos: number;
	turno: Turno | null;
	notas: string | null;
}

export interface FilaBajada extends FilaBase {
	entidad: 'cambio_carro';
	carro_id: string | null;
	/** El número del carro como texto, si lo propio lo trae. */
	carro: string | null;
}

export type FilaProduccionReciente = FilaSilo | FilaPesada | FilaParada | FilaBajada;

export interface TotalesProduccion {
	silos_cerrados: number;
	neto_cerrados_g: number;
	silos_descartados: number;
	pesadas: number;
	/** Solo las pesadas que suman (`no_suma === null`). */
	kilos_turno_g: number;
	/** Cuántas pesadas quedaron afuera de los kilos, con su motivo. */
	pesadas_que_no_suman: number;
	paradas: number;
	minutos_parada: number;
	bajadas: number;
}

export interface ProyeccionDeProduccion {
	filas: FilaProduccionReciente[];
	/** Sobre todas las filas; con un filtro, la pantalla recalcula sobre las visibles (`totalesDeProduccion`). */
	totales: TotalesProduccion;
	personas: string[];
	/** Lo propio fechado después de `hasta`: no se lista ni se suma; la pantalla lo cuenta aparte. */
	fuera_de_rango: number;
}

/**
 * Lo que el teléfono sabe aparte del bloque, para decidir si una pesada propia suma: el lote de cada
 * silo abierto (`llenados_abiertos`) y el tipo de cada lote (`lotes_campo`). Sin dato, la pesada NO suma.
 */
export interface ContextoDeProduccion {
	loteDelSilo?: ReadonlyMap<string, string>;
	tipoDeLote?: ReadonlyMap<string, TipoDeLote>;
}

function texto(payload: unknown, clave: string): string | null {
	if (typeof payload !== 'object' || payload === null) return null;
	const valor = (payload as Record<string, unknown>)[clave];
	return typeof valor === 'string' ? valor : null;
}

function cifra(payload: unknown, clave: string): number | null {
	if (typeof payload !== 'object' || payload === null) return null;
	const valor = (payload as Record<string, unknown>)[clave];
	return typeof valor === 'number' ? valor : null;
}

function esTurno(valor: string | null): valor is Turno {
	return valor === 'maniana' || valor === 'tarde' || valor === 'noche';
}

function unicos(...listas: string[][]): string[] {
	const vistas: string[] = [];
	for (const lista of listas) for (const id of lista) if (!vistas.includes(id)) vistas.push(id);
	return vistas;
}

function filaDeSiloDelCatalogo(s: SiloReciente, dispositivoId: string | null): FilaSilo {
	const hora = s.cerrado_en ?? s.abierto_en;
	return {
		clave: `catalogo:llenado:${s.llenado_id}`,
		entidad: 'llenado',
		hora,
		orden: hora,
		personas: s.estado === 'abierto' ? [s.abierto_por] : unicos([s.abierto_por], [s.persona_id]),
		este_telefono: dispositivoId !== null && s.dispositivo_id === dispositivoId,
		origen: 'catalogo',
		corregido: s.corregido === 1,
		llenado_id: s.llenado_id,
		precinto: s.precinto,
		silo: null,
		silo_fisico_id: s.silo_fisico_id,
		trieur_id: s.trieur_id,
		lote_campo_id: s.lote_campo_id,
		lote_tipo: s.lote_tipo,
		estado: s.estado,
		abierto_en: s.abierto_en,
		cerrado_en: s.cerrado_en,
		tara_base_g: s.tara_base_g,
		ultimo_bruto_g: s.ultimo_bruto_g,
		neto_g: s.neto_g,
		motivo_descarte: s.motivo_descarte,
	};
}

/** Un recibo entra a la máquina de estados del silo si el snapshot no lo refleja; una pendiente, siempre. */
function noReflejado(p: PropioDeProduccion, horaDelCatalogo: string | undefined): boolean {
	if (p.pendiente) return true;
	if (horaDelCatalogo === undefined || p.sincronizado_en === undefined) return true;
	return p.sincronizado_en > horaDelCatalogo;
}

function tipoDeLoteDe(loteId: string | null, contexto: ContextoDeProduccion): TipoDeLote | null {
	if (loteId === null) return null;
	return contexto.tipoDeLote?.get(loteId) ?? null;
}

/** La regla de `CTE_PESADAS`, con «no se sabe» como no: lo que no se puede afirmar no entra al ticket. */
function motivoNoSuma(tipo: TipoDeLote | null, estado: EstadoDeSilo | undefined): MotivoNoSuma | null {
	if (estado === 'descartado') return 'descartado';
	if (tipo === 'blendeo') return 'blendeo';
	if (tipo === null) return 'lote_desconocido';
	return null;
}

/**
 * La fusión (§5.5, «Producción reciente»):
 * - los silos del bloque son la base; un `llenado.abrir` propio no reflejado crea la fila, un
 *   `cerrar` o `descartar` la muta (o la crea desde el resumen si el bloque no la trae), y un
 *   `parcial.crear` le actualiza el bruto; las mutaciones se aplican en el orden de la cola;
 * - pesadas, paradas y bajadas: regla 1 para el catálogo; reglas 2 y 3 para lo propio, con la
 *   identidad `raiz_id` (o el id del registro en una pendiente, que ES su raíz futura: una mutación
 *   aplicada cuyo acuse se perdió no sale dos veces);
 * - regla 0 para todo, con los bordes del bloque: lo propio después de `hasta` se cuenta aparte;
 * - regla 4 para el orden.
 * Una pesada es de quien ENTREGÓ el turno (los kilos son de ese turno); quien recibió va como texto.
 */
export function proyectarProduccionReciente(
	bloque: ProduccionReciente,
	propios: PropioDeProduccion[],
	horaDelCatalogo: string | undefined,
	dispositivoId: string | null,
	contexto: ContextoDeProduccion = {},
): ProyeccionDeProduccion {
	const { desde, hasta } = bloque;
	const enVentana = (hora: string) => ubicarEnVentana(hora, desde, hasta) === 'dentro';

	const silos = new Map<string, FilaSilo>();
	for (const s of bloque.silos) {
		if (!enVentana(s.abierto_en) && !(s.cerrado_en !== null && enVentana(s.cerrado_en))) continue;
		silos.set(s.llenado_id, filaDeSiloDelCatalogo(s, dispositivoId));
	}

	const raices = new Set<string>([...bloque.pesadas.map((p) => p.raiz_id), ...bloque.paradas.map((p) => p.raiz_id), ...bloque.bajadas.map((b) => b.raiz_id)]);
	const sueltas: FilaProduccionReciente[] = [];
	for (const p of bloque.pesadas) {
		if (!enVentana(p.fin_en)) continue;
		sueltas.push({
			clave: `catalogo:parcial:${p.raiz_id}`,
			entidad: 'parcial',
			hora: p.fin_en,
			orden: p.fin_en,
			personas: [p.entregada_por],
			este_telefono: dispositivoId !== null && p.dispositivo_id === dispositivoId,
			origen: 'catalogo',
			corregido: p.corregido === 1,
			llenado_id: p.llenado_id,
			precinto: p.precinto,
			silo_fisico_id: p.silo_fisico_id,
			turno: p.turno,
			fin_en: p.fin_en,
			bruto_g: p.bruto_g,
			del_turno_g: p.del_turno_g,
			es_cierre: p.es_cierre === 1,
			entregada_por: p.entregada_por,
			recibida_por: p.recibida_por,
			no_suma: motivoNoSuma(p.lote_tipo, p.estado_del_silo),
		});
	}
	for (const p of bloque.paradas) {
		if (!enVentana(p.registrado_en)) continue;
		sueltas.push({
			clave: `catalogo:parada:${p.raiz_id}`,
			entidad: 'parada',
			hora: p.registrado_en,
			orden: p.registrado_en,
			personas: [p.persona_id],
			este_telefono: dispositivoId !== null && p.dispositivo_id === dispositivoId,
			origen: 'catalogo',
			corregido: p.corregido === 1,
			motivo_parada_id: p.motivo_parada_id,
			motivo: null,
			minutos: p.minutos,
			turno: p.turno,
			notas: p.notas,
		});
	}
	for (const b of bloque.bajadas) {
		if (!enVentana(b.registrado_en)) continue;
		sueltas.push({
			clave: `catalogo:cambio_carro:${b.raiz_id}`,
			entidad: 'cambio_carro',
			hora: b.registrado_en,
			orden: b.registrado_en,
			personas: [b.persona_id],
			este_telefono: dispositivoId !== null && b.dispositivo_id === dispositivoId,
			origen: 'catalogo',
			corregido: b.corregido === 1,
			carro_id: b.carro_id,
			carro: null,
		});
	}

	let fueraDeRango = 0;
	/** Las pesadas propias se resuelven al final contra el silo que quedó: el cierre o el descarte pueden venir después. */
	const pesadasPropias: FilaPesada[] = [];
	for (const p of propios) {
		const orden = p.hora ?? ordenDelAcuse(p.sincronizado_en);
		const donde = ubicarEnVentana(orden, desde, hasta);
		if (donde === 'despues') {
			fueraDeRango += 1;
			continue;
		}
		if (donde === 'antes') continue;
		const origen = p.pendiente ? ('pendiente' as const) : ('recibo' as const);
		const base = { hora: p.hora, orden, personas: p.personas, este_telefono: true, origen, corregido: false, ...marcaDePendiente(p) };
		const clave = `${origen}:${p.entidad}:${p.registro_id}`;
		const llenadoId = texto(p.payload, 'llenado_id');

		if (p.entidad === 'llenado') {
			if (llenadoId === null || !noReflejado(p, horaDelCatalogo)) continue;
			const existente = silos.get(llenadoId);
			if (p.tipo === 'llenado.abrir') {
				if (existente) continue; // el catálogo ya lo trae: manda él
				const loteId = texto(p.payload, 'lote_campo_id');
				silos.set(llenadoId, {
					...base,
					clave,
					entidad: 'llenado',
					llenado_id: llenadoId,
					precinto: texto(p.payload, 'precinto') ?? p.resumen.precinto ?? '—',
					silo: p.resumen.silo ?? null,
					silo_fisico_id: texto(p.payload, 'silo_fisico_id'),
					trieur_id: texto(p.payload, 'trieur_id'),
					lote_campo_id: loteId,
					lote_tipo: tipoDeLoteDe(loteId, contexto),
					estado: 'abierto',
					abierto_en: texto(p.payload, 'abierto_en'),
					cerrado_en: null,
					tara_base_g: cifra(p.payload, 'tara_base_g'),
					ultimo_bruto_g: null,
					neto_g: null,
					motivo_descarte: null,
				});
			} else if (p.tipo === 'llenado.cerrar' || p.tipo === 'llenado.descartar') {
				const estado: EstadoDeSilo = p.tipo === 'llenado.cerrar' ? 'cerrado' : 'descartado';
				const cerradoEn = texto(p.payload, 'cerrado_en');
				const motivo = texto(p.payload, 'motivo_descarte');
				if (existente) {
					existente.estado = estado;
					existente.cerrado_en = cerradoEn ?? existente.cerrado_en;
					existente.neto_g = p.resumen.neto_g ?? existente.neto_g;
					existente.motivo_descarte = motivo ?? existente.motivo_descarte;
					existente.personas = unicos(existente.personas, p.personas);
					existente.hora = existente.cerrado_en ?? existente.abierto_en;
					existente.orden = existente.hora ?? orden;
					existente.origen = origen;
					existente.este_telefono = true;
					existente.corregido = false;
					delete existente.id;
					delete existente.epoch;
					Object.assign(existente, marcaDePendiente(p));
				} else {
					const intervalo = p.resumen.intervalo;
					const loteId = intervalo?.lote_campo_id ?? null;
					silos.set(llenadoId, {
						...base,
						clave,
						entidad: 'llenado',
						llenado_id: llenadoId,
						precinto: p.resumen.precinto ?? '—',
						silo: p.resumen.silo ?? null,
						silo_fisico_id: intervalo?.silo_fisico_id ?? null,
						trieur_id: intervalo?.trieur_id ?? null,
						lote_campo_id: loteId,
						lote_tipo: tipoDeLoteDe(loteId, contexto),
						estado,
						abierto_en: intervalo?.abierto_en ?? null,
						cerrado_en: cerradoEn,
						tara_base_g: null,
						ultimo_bruto_g: null,
						neto_g: p.resumen.neto_g ?? null,
						motivo_descarte: motivo,
					});
				}
			}
			continue;
		}

		const raiz = p.raiz_id ?? p.registro_id;
		if (raices.has(raiz) || !entraElPropio(p, raices, horaDelCatalogo)) continue;

		if (p.entidad === 'parcial') {
			if (llenadoId === null) continue;
			const bruto = cifra(p.payload, 'bruto_g') ?? p.resumen.bruto_g ?? 0;
			const silo = silos.get(llenadoId);
			if (silo) {
				silo.ultimo_bruto_g = bruto;
				silo.neto_g = silo.tara_base_g === null ? silo.neto_g : bruto - silo.tara_base_g;
			}
			const turno = texto(p.payload, 'turno');
			pesadasPropias.push({
				...base,
				clave,
				entidad: 'parcial',
				llenado_id: llenadoId,
				precinto: p.resumen.precinto ?? silo?.precinto ?? '—',
				silo_fisico_id: silo?.silo_fisico_id ?? null,
				turno: esTurno(turno) ? turno : null,
				fin_en: texto(p.payload, 'fin_en'),
				bruto_g: bruto,
				del_turno_g: p.resumen.del_turno_g ?? 0,
				es_cierre: false,
				entregada_por: texto(p.payload, 'entregada_por'),
				recibida_por: texto(p.payload, 'recibida_por'),
				no_suma: 'lote_desconocido',
			});
		} else if (p.entidad === 'parada') {
			const turno = texto(p.payload, 'turno');
			sueltas.push({
				...base,
				clave,
				entidad: 'parada',
				motivo_parada_id: texto(p.payload, 'motivo_parada_id'),
				motivo: p.resumen.motivo ?? null,
				minutos: cifra(p.payload, 'minutos') ?? p.resumen.minutos ?? 0,
				turno: esTurno(turno) ? turno : null,
				notas: texto(p.payload, 'notas'),
			});
		} else if (p.entidad === 'cambio_carro') {
			sueltas.push({ ...base, clave, entidad: 'cambio_carro', carro_id: texto(p.payload, 'carro_id'), carro: p.resumen.carro ?? null });
		}
	}

	// Lo que una pesada propia sabe recién al final, según cómo quedó el silo: si es la del cierre, y si suma.
	for (const pesada of pesadasPropias) {
		const silo = silos.get(pesada.llenado_id);
		pesada.es_cierre = silo !== undefined && silo.cerrado_en !== null && pesada.fin_en === silo.cerrado_en;
		const tipo = silo?.lote_tipo ?? tipoDeLoteDe(silo?.lote_campo_id ?? contexto.loteDelSilo?.get(pesada.llenado_id) ?? null, contexto);
		pesada.no_suma = motivoNoSuma(tipo, silo?.estado);
	}

	const filas = ordenarRecientes<FilaProduccionReciente>([...silos.values(), ...sueltas, ...pesadasPropias]);
	return { filas, totales: totalesDeProduccion(filas), personas: personasDe(filas), fuera_de_rango: fueraDeRango };
}

/**
 * Los totales de un conjunto de filas: el ticket de la pantalla se calcula sobre las que se ven, así con
 * una persona o un tipo elegido dice lo de ese recorte y no lo de la estación (como en verde).
 */
export function totalesDeProduccion(filas: FilaProduccionReciente[]): TotalesProduccion {
	const t: TotalesProduccion = {
		silos_cerrados: 0,
		neto_cerrados_g: 0,
		silos_descartados: 0,
		pesadas: 0,
		kilos_turno_g: 0,
		pesadas_que_no_suman: 0,
		paradas: 0,
		minutos_parada: 0,
		bajadas: 0,
	};
	for (const f of filas) {
		switch (f.entidad) {
			case 'llenado':
				if (f.estado === 'cerrado') {
					t.silos_cerrados += 1;
					t.neto_cerrados_g += f.neto_g ?? 0;
				} else if (f.estado === 'descartado') t.silos_descartados += 1;
				break;
			case 'parcial':
				t.pesadas += 1;
				if (f.no_suma === null) t.kilos_turno_g += f.del_turno_g;
				else t.pesadas_que_no_suman += 1;
				break;
			case 'parada':
				t.paradas += 1;
				t.minutos_parada += f.minutos;
				break;
			case 'cambio_carro':
				t.bajadas += 1;
				break;
		}
	}
	return t;
}
